import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress
} from '@mui/material'
import { payrollApi } from '../../../api/payrollApi'
import { employeeApi } from '../../../api/employeeApi'
import toast from 'react-hot-toast'

const GeneratePayrollDialog = ({ open, onClose, onSuccess }) => {
  const [employees, setEmployees] = useState([])
  const [loadingEmployees, setLoadingEmployees] = useState(false)
  const [generating, setGenerating] = useState(false)

  const [formData, setFormData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    employee_id: ''
  })

  useEffect(() => {
    if (open) {
      fetchEmployees()
    }
  }, [open])

  const fetchEmployees = async () => {
    setLoadingEmployees(true)
    try {
      const response = await employeeApi.listEmployees({ limit: 1000 })
      const empData = response.data?.employees || []
      setEmployees(empData)
    } catch (error) {
      console.error('Failed to fetch employees', error)
      toast.error('Could not load employee list')
    } finally {
      setLoadingEmployees(false)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async () => {
    if (!formData.employee_id) {
      toast.error('Please select an employee')
      return
    }

    setGenerating(true)
    try {
      await payrollApi.generatePayroll({
        month: parseInt(formData.month),
        year: parseInt(formData.year),
        employee_id: parseInt(formData.employee_id)
      })
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Error generating payroll', error)
    } finally {
      setGenerating(false)
    }
  }

  const currentYear = new Date().getFullYear()
  const years = Array.from(new Array(5), (val, index) => currentYear - index)

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ]

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate Payroll</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Employee *</InputLabel>
            <Select
              name="employee_id"
              value={formData.employee_id}
              label="Employee *"
              onChange={handleChange}
            >
              {loadingEmployees ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 2 }} /> Loading...
                </MenuItem>
              ) : (
                employees.map(emp => (
                  <MenuItem key={emp.id || emp.employee_id} value={emp.id || emp.employee_id}>
                    {emp.employee_code} - {emp.first_name} {emp.last_name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Month</InputLabel>
              <Select
                name="month"
                value={formData.month}
                label="Month"
                onChange={handleChange}
              >
                {months.map(m => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                name="year"
                value={formData.year}
                label="Year"
                onChange={handleChange}
              >
                {years.map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Typography variant="body2" color="textSecondary">
            This action will generate payroll based on the attendance and leave records for the selected month.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={generating}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={generating || !formData.employee_id}
          startIcon={generating ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {generating ? 'Generating...' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default GeneratePayrollDialog
